import { Injectable } from '@angular/core';
import { ConfigService } from '../../services/config.service';
import { ModalOptionsConfig } from '../../../lean-shared/components/nz-modal-service/modal.service';
import { BehaviorSubject, firstValueFrom, ReplaySubject } from 'rxjs';
import { SelectAdaptor } from '../../../kamona/components/kamona-input-field/kamona-input-field.component';
import { LocalStorageService } from '../../services/local-storage.service';
import { AppConfig } from '../../../shared/config/app-config';
import { FVSFeatureFlagService } from './fvs-feature-flags.service';
import { UserAlias } from '../../../shared/constants';
import { LGA, FVSLocation, PaginationParam, PriorityBOsFilterParam } from '../../models/fvs-address-verification.model';
import { StringHelper } from '../../helpers/strings.helper';
import { convertLGAaToTableMap } from '../../../shared/models/nav-item-v2.helper';

@Injectable({
    providedIn: 'root',
})
export class FVSHelperService extends FVSFeatureFlagService {
    readonly defaultStartPage = 0;
    readonly defaultPageSize = 5;
    private states = new ReplaySubject<FVSLocation[]>(1);
    private lgas = new ReplaySubject<LGA[]>(1);
    private lgasByState = new ReplaySubject<SelectAdaptor[]>(1);

    private priorityBOsFilterParam = new BehaviorSubject<Partial<PriorityBOsFilterParam> | undefined>(undefined);
    private priorityBOsPageParam = new BehaviorSubject<PaginationParam>({ page: this.defaultStartPage, size: this.defaultPageSize });
    private priorityBOsURLParam = new BehaviorSubject<string>('');

    priorityBOsFilterParam$ = this.priorityBOsFilterParam.asObservable();
    priorityBOsURLParam$ = this.priorityBOsURLParam.asObservable();
    priorityBOsPageParam$ = this.priorityBOsPageParam.asObservable();

    state$ = this.states.asObservable();
    lgas$ = this.lgas.asObservable();
    lgasByState$ = this.lgasByState.asObservable();

    private lgaNameMap: Record<string, string> = {};

    constructor(private configService: ConfigService, private localStorageService: LocalStorageService, public config: AppConfig) {
        super(config);
    }

    async initializeConfigService() {
        try {
            const response = await this.configService.getConfig();

            this.states.next(response?.states ?? []);
            this.lgas.next(response?.localGovernments ?? []);

            this.lgaNameMap = convertLGAaToTableMap(response?.localGovernments);
        } catch (error) {
            this.states.next([]);
            this.lgas.next([]);
        }
    }

    async setLGAByStateCode(code: string) {
        const lgas = await firstValueFrom(this.lgas$);

        const lgasByState = lgas.filter((lga) => lga.state.code === code);

        this.lgasByState.next(lgasByState.map((lga) => ({ label: lga.name, value: lga.code })));
    }

    getNamesByLGACodes(codes: string[]) {
        return codes.map((code) => this.lgaNameMap[code]).join(', ');
    }

    configureModalOptions(lgaNames: string) {
        const config = new ModalOptionsConfig();
        config.nzComponentParams.alertType = 'info';
        config.nzTitle = 'Confirm Selection';
        config.nzComponentParams.title = 'Confirm Selection';
        config.nzComponentParams.message = `Please confirm your selection of LGAs, ensuring they are accessible and close to you. You will be assigned verifications within these areas. ${lgaNames}`;
        config.nzComponentParams.callbackName = 'Confirm';
        config.nzComponentParams.cancelName = 'Close';

        return config;
    }

    routeToLGAUpdateIfAllowed() {
        const showFVSOnboard = this.getLGAUpdateFeatureFlag();

        return showFVSOnboard;
    }

    reRouteToReUpdateBRMLGAOnFVS() {
        return this.getAllowBRMReUpdateLGAOnFVS();
    }

    routeToAddressVerificationIfAllowed() {
        const showFVSAddressVerification = this.getFVSAddressVerificationFeatureFlag();
        const hasBRMRole = this.localStorageService.getUserRole() === UserAlias.aggregator;

        return showFVSAddressVerification && hasBRMRole;
    }

    configURLPageParam({
        pagination,
        filter,
        config,
    }: {
        pagination?: Partial<PaginationParam>;
        filter?: Partial<PriorityBOsFilterParam>;
        config?: {
            resetPagination?: boolean;
        };
    }) {
        const { page = this.defaultStartPage, size = this.defaultPageSize } = pagination || {};
        const newPage = config?.resetPagination ? this.defaultStartPage : page;
        const newSize = config?.resetPagination ? this.defaultPageSize : size;

        this.modifyPagination(newPage, newSize);
        this.modifyFilter(filter);

        const urlParam = StringHelper.toUrlString({ page: newPage, size: newSize, ...filter });

        this.priorityBOsURLParam.next(urlParam);
    }

    modifyPagination(page = this.defaultStartPage, size = this.defaultPageSize) {
        this.priorityBOsPageParam.next({ page, size });
    }

    modifyFilter(filter?: Partial<PriorityBOsFilterParam>) {
        this.priorityBOsFilterParam.next(filter);
    }
}
